import { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { currencyOption, generateDaysArray, RealStateTransaction } from "../../../../../utility/data";
import { addTransaction, getUserAssets, updateTransaction } from "../../../../../services/transaction";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { formatNumber } from "../../../../../utility/helper";
import { fetchGroups, resetGroupsState } from "../../../../../redux/slices/groupsSlice";

const CreateTransactionModal = ({ visible, singleTransactionData, actionTypes, fetchTransactionData, fetchFutureTransactionData, GetstatsData, GetRoiData, statsData, viewTransaction, onHide, SelectType, loading, setLoading, toast, assetName }) => {
  const { groupsData, groupLoading } = useSelector((state) => state.groups);
  const { currency } = useSelector((state) => state.user);
  const InitialDateValue = viewTransaction?.transactions?.find((item) => item.is_initial)
  const filteredData = groupsData?.filter(group => group?.id == assetName?.included_in);
  const accessToken = JSON.parse(localStorage.getItem('access_token'));

  const initialTransactionData = {
    assetType: RealStateTransaction[0],
    includeIn: filteredData[0] ? filteredData[0] : groupsData[0],
    date: moment().toDate(),
    type: singleTransactionData?.type ? singleTransactionData?.type : SelectType,
    multiples: 0,
    currency: currency ? currency : currencyOption[0].value,
    reason: singleTransactionData?.remark ? singleTransactionData?.remark : "",
    remark: singleTransactionData?.remark ? singleTransactionData?.remark : "",
    EBITDA: 0,
    noi: 0,
    capRate: 0,
    value: singleTransactionData.value ? formatNumber(singleTransactionData.value) : 0,
    p_and_l: singleTransactionData.p_and_l ? formatNumber(singleTransactionData.p_and_l) : 0,
    isRecurring: singleTransactionData.is_recurring ? singleTransactionData.is_recurring : false,
    recurringDay: singleTransactionData.recurring_day ? singleTransactionData.recurring_day : 0,
    cashAssets: singleTransactionData.cash_asset_id ? singleTransactionData.cash_asset_id : "",
  };

  const [createTransactionData, setCreateTransactionData] = useState(initialTransactionData);
  const [errors, setErrors] = useState({});
  const [recurringDays, setRecurringDays] = useState([]);
  const [userAssets, setUserAssets] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (singleTransactionData) {
      setCreateTransactionData((prevData) => ({
        ...prevData,
        currency: singleTransactionData.currency || prevData.currency,
        date: singleTransactionData.date ? moment(singleTransactionData.date).toDate() : prevData.date,
        reason: singleTransactionData.remark || "",
        value: formatNumber(singleTransactionData.value) || "",
        p_and_l: formatNumber(singleTransactionData.p_and_l) || "",
        type: singleTransactionData.type || "",
        cashAssets: singleTransactionData.cash_asset_id || "",
        isRecurring: singleTransactionData.is_recurring || false,
        recurringDay: singleTransactionData.recurring_day || 0,
      }));
    }
  }, [singleTransactionData]);


  useEffect(() => {
    if (singleTransactionData && userAssets.length > 0) {
      const selectedAsset = userAssets.find(asset => asset?.id === singleTransactionData?.cash_asset_id);

      setCreateTransactionData((prevData) => ({
        ...prevData,
        cashAssets: selectedAsset ? selectedAsset.id : prevData.cashAssets,
      }));
    }
  }, [singleTransactionData, userAssets]);


  useEffect(() => {
    if (currency && currency !== createTransactionData.currency) {
      setCreateTransactionData((prevData) => ({
        ...prevData,
        currency,
      }));
    }
  }, [currency]);

  useEffect(() => {
    GetUserAssetsData();
  }, []);

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchGroups(accessToken));
    }
    return () => {
      dispatch(resetGroupsState());
    };
  }, [dispatch, accessToken]);

  useEffect(() => {
    setCreateTransactionData((prevData) => ({
      ...prevData,
      type: singleTransactionData?.type ?? SelectType,
    }));
  }, [SelectType, visible]);


  useEffect(() => {
    if (groupsData.length > 0) {
      setCreateTransactionData((prev) => ({
        ...prev,
        includeIn: filteredData[0] ? filteredData[0] : groupsData[0],
      }));
    }
  }, [groupsData, assetName]);


  useEffect(() => {
    if (createTransactionData.isRecurring) {
      const days = generateDaysArray(createTransactionData.date);
      setRecurringDays(days);

      if (createTransactionData.recurring > days.length) {
        handleInputChange({ name: "recurring", value: days.length });
      }
    }
  }, [createTransactionData.date, createTransactionData.isRecurring]);

  const GetUserAssetsData = async () => {
    setLoading(true);
    try {
      const response = await getUserAssets(1, accessToken);
      if (response.status === 200) {
        const data = response?.data.data;
        setUserAssets(data);
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target || e;

    setCreateTransactionData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name] && value && value.toString().trim()) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  const handlePLChange = (e) => {
    const { name } = e.target;
    let value = e.target.value;

    // Allow only numbers, a single decimal point, and an optional leading '-'
    let rawValue = value.replace(/[^0-9.-]/g, '');

    // Ensure '-' is only at the start
    if (rawValue.includes('-') && rawValue.indexOf('-') !== 0) {
      rawValue = rawValue.replace(/-/g, ''); // Remove invalid dashes
    }

    // Prevent multiple decimal points
    const decimalCount = (rawValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }

    // Format value (assuming formatNumber properly handles negatives)
    value = formatNumber(rawValue);

    // Update state
    setCreateTransactionData((prevData) => ({
      ...prevData,
      p_and_l: value,
    }));

    // Remove error if input is valid, otherwise keep the error
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (value.trim()) {
        delete updatedErrors[name];
      } else {
        updatedErrors[name] = "P&L is required"; // Ensure an error exists if empty
      }
      return updatedErrors;
    });
  };


  const handleValueChange = (e) => {
    const { name } = e.target;
    let value = e.target.value;

    // Allow only numbers and a single decimal point
    let rawValue = value.replace(/[^0-9.-]/g, ''); // Allow '-' for negative numbers

    // Prevent multiple decimals
    const decimalCount = (rawValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }

    // Format value
    value = formatNumber(rawValue);

    let AutoCalPL = null;
    if (SelectType === "VALUATION" || SelectType === "SELL") {
      AutoCalPL = formatNumber(Number(rawValue) - Number(statsData?.initial_value));
    }

    setCreateTransactionData((prevData) => ({
      ...prevData,
      value: value,
      ...(AutoCalPL !== null && { p_and_l: AutoCalPL })
    }));

    // Remove errors if input is valid
    if (errors[name] && value.trim()) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };


  const handleAssetTypeChange = (e) => {
    const selectedAssetType = e.value;

    setCreateTransactionData((prevData) => ({
      ...prevData,
      assetType: selectedAssetType,
    }));
  };


  const handleCashAssetChange = (e) => {
    setCreateTransactionData((prevData) => ({
      ...prevData,
      cashAssets: e.value,
    }));
  };


  const validateForm = () => {
    const newErrors = {};

    if (!createTransactionData.date) {
      newErrors.date = "Date is required";
    }
    if (!createTransactionData.type) {
      newErrors.type = "Type is required";
    }
    if (!createTransactionData.currency) {
      newErrors.currency = "Currency is required";
    }

    if (!createTransactionData.includeIn) {
      newErrors.includeIn = "groups is required";
    }
    if (!createTransactionData.value) {
      newErrors.value = "value is required";
    }

    if (!createTransactionData.p_and_l && (SelectType == "VALUATION" || SelectType == "SELL")) {
      newErrors.p_and_l = "P&L is required";
    }

    if ((createTransactionData.type == "IN" || createTransactionData.type == "OUT") && !createTransactionData.value) {
      newErrors.value = "Value is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSave = async () => {
    if (validateForm()) {
      setLoading(true);
      if (!assetName) {
        setLoading(false);
        toast.current.show({
          className: 'bg-yellow-700 text-white',
          severity: 'warn',
          summary: 'Warning',
          detail: "Please add the first initial transaction",
          life: 3000,
        });
        return;
      }

      const formattedData = {
        ...createTransactionData,
        p_and_l: createTransactionData.p_and_l ? createTransactionData.p_and_l.replace(/[,']/g, '') : '',
        value: createTransactionData.value ? createTransactionData.value.replace(/[,']/g, '') : '',
        type: createTransactionData.type ? createTransactionData.type : '',
        date: createTransactionData.date
          ? moment(createTransactionData.date).format('YYYY-MM-DD HH:mm:ss')
          : null,
      };

      try {
        const response = await (actionTypes === "Create"
          ? addTransaction(formattedData, assetName, accessToken)
          : updateTransaction(singleTransactionData.id, formattedData, assetName, accessToken));

        if (response.data.status === true) {
          fetchTransactionData();
          fetchFutureTransactionData();
          GetstatsData();
          GetRoiData();
          handleClose();
          toast.current.show({
            className: 'bg-green-300 text-white',
            severity: 'success',
            summary: 'Success',
            detail: response.data.message,
            life: 3000,
          });
        }
      } catch (error) {
        console.error('Error adding transaction:', error);
        toast.current.show({
          className: 'bg-yellow-700 text-white',
          severity: 'warn',
          summary: 'Warning',
          detail: error.message,
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  if (!visible) return null;

  const handleClose = () => {
    setCreateTransactionData(initialTransactionData);
    setErrors({});
    onHide();
  };

  return (
    <div>
      <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-[rgba(44,52,79,1)] md:w-1/2 w-1/2 shadow-lg p-6 text-[rgba(255,255,255,1)]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-2xl">{actionTypes} Transaction</h2>
            <FaTimes onClick={handleClose} className="cursor-pointer" fontSize={26} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="col-span-1 flex items-center gap-5 mb-4">
              <label className="text-sm font-medium text-white w-1/4">Asset Type</label>
              <Dropdown
                value={createTransactionData.assetType}
                name="assetType"
                onChange={handleAssetTypeChange}
                options={RealStateTransaction}
                optionLabel="name"
                placeholder="Select asset type"
                className={`rounded-lg w-full p-0 bg-transparent border border-gray-300 text-white focus:outline-none focus:border-b-2 ${createTransactionData.assetType ? "asset_Dropdown" : ""}`}
              />
            </div>
            <div className="flex flex-col">
              <div className="col-span-1 flex items-center gap-5 mb-4">
                <label className="text-sm font-medium text-white w-1/4">Include In*</label>
                <Dropdown
                  value={createTransactionData.includeIn}
                  name="includeIn"
                  onChange={(e) => handleInputChange({ name: "includeIn", value: e.value })}
                  options={groupsData}
                  disabled
                  optionLabel="name"
                  className={`w-full p-0 bg-transparent border border-gray-300 text-white rounded-lg focus:outline-none focus:border-b-2 ${createTransactionData.includeIn ? "asset_Dropdown" : ""}`}
                />
              </div>
              {errors.includeIn && <small className="text-red-500 text-center mt-[-10px]">{errors.includeIn}</small>}
            </div>
            <div className="flex flex-col">
              <div className="col-span-1 flex items-center gap-5 mb-4">
                <label className="text-sm font-medium text-white w-1/4">Date*</label>
                <Calendar
                  value={createTransactionData.date}
                  onChange={(e) => handleInputChange({ name: "date", value: e.value })}
                  dateFormat="dd/mm/yy"
                  className="asset_date w-full"
                  maxDate={moment().toDate()}
                  minDate={InitialDateValue?.date ? moment(InitialDateValue.date).toDate() : moment().toDate()}
                  disabled={actionTypes === "Update"}
                />

              </div>
              {errors.date && <small className="text-red-500 text-center mt-[-10px]">{errors.date}</small>}
            </div>

            <div className="flex flex-col">
              <div className="col-span-1 flex items-center gap-5 mb-4">
                <label className="text-sm font-medium text-white w-1/4">Type*</label>
                <input
                  type="text"
                  name="type"
                  value={createTransactionData.type}
                  onChange={handleInputChange}
                  disabled
                  autoComplete="off"
                  className="block w-full bg-transparent border-b disabled:text-gray-500 border-gray-300 text-white focus:outline-none focus:border-b-2"
                />
              </div>
              {errors.type && <small className="text-red-500 text-center mt-[-10px]">{errors.type}</small>}
            </div>

            <div className="flex flex-col">
              <div className="col-span-1 flex items-center gap-5 mb-4">
                <label className="text-sm font-medium text-white w-1/4">Reason</label>
                <input
                  type="text"
                  name="reason"
                  value={createTransactionData.reason}
                  onChange={handleInputChange}
                  autoComplete="off"
                  className="block w-full bg-transparent border-b border-gray-300 text-white focus:outline-none focus:border-b-2"
                />
              </div>
              {errors.reason && <small className="text-red-500 text-center mt-[-10px]">{errors.reason}</small>}
            </div>
            <div className="flex md:flex-col flex-row">
              <div className="col-span-1 md:flex items-center gap-5 mb-4">
                <label className="text-sm font-medium text-white w-1/4">Currency*</label>
                <Dropdown
                  value={createTransactionData.currency}
                  name="currency"
                  onChange={handleInputChange}
                  disabled
                  options={currencyOption}
                  optionLabel="label"
                  optionValue="value"
                  className={`create_transaction w-full p-0 bg-transparent border-b border-gray-300 text-white rounded-none focus:outline-none focus:border-b-2 ${createTransactionData.assetType ? "asset_Dropdown" : ""}`}
                />
              </div>
              {errors.currency && <small className="text-red-500 text-center mt-[-10px]">{errors.currency}</small>}
            </div>
            <div className="flex flex-col">
              <div className="col-span-1 flex gap-5 mb-4">
                <label className="text-sm font-medium text-white capitalize w-1/4">Value*</label>
                <input
                  type="text"
                  name="value"
                  value={createTransactionData.value}
                  onChange={handleValueChange}
                  className="block w-full bg-transparent border-b border-gray-300 text-white focus:outline-none focus:border-b-2"
                />
              </div>
              {errors.value && <small className="text-red-500 text-center mt-[-10px]">{errors.value}</small>}
            </div>
            {(SelectType === "VALUATION" || SelectType === "SELL") && (
              <div className="flex flex-col">
                <div className="col-span-1 flex gap-5 mb-4">
                  <label className="text-sm font-medium text-white capitalize w-1/4">P&L*</label>
                  <input
                    type="text"
                    name="p_and_l"
                    value={createTransactionData.p_and_l}
                    onChange={handlePLChange}
                    className="block w-full bg-transparent border-b border-gray-300 text-white focus:outline-none focus:border-b-2"
                  />
                </div>
                {errors.p_and_l && <small className="text-red-500 text-center mt-[-10px]">{errors.p_and_l}</small>}
              </div>
            )}
            {(actionTypes === "Create" || (actionTypes === "Update" && singleTransactionData?.is_type_recurring)) && (
              (SelectType === "INCOME") && (
                <div className="flex flex-col">
                  <div className="col-span-1 flex gap-5 mb-4">
                    <label className="text-sm font-medium text-white capitalize w-1/4">Recurring</label>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createTransactionData.isRecurring}
                        onChange={(e) => handleInputChange({
                          name: "isRecurring",
                          value: e.target.checked
                        })}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )
            )}

            {(SelectType === "SELL") && (
              <div className="flex flex-col">
                <div className="col-span-1 flex gap-5 mb-4">
                  <label className="text-sm font-medium text-white capitalize w-1/4">Cash Assets</label>
                  <Dropdown
                    value={createTransactionData.cashAssets}
                    showClear
                    name="cashAssets"
                    onChange={handleCashAssetChange}
                    options={userAssets}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select a cash assets"
                    className={`w-full bg-transparent border border-gray-300 rounded-lg ${createTransactionData.cashAssets ? "asset_Dropdown" : ""}`}
                  />
                </div>
              </div>
            )}

            {createTransactionData.isRecurring && (
              <div className="flex flex-col">
                <div className="col-span-1 flex gap-3 mb-4">
                  <label className="text-sm font-medium text-white capitalize w-1/4">Recurring Day*</label>
                  <Dropdown
                    value={createTransactionData.recurringDay}
                    name="recurringDay"
                    onChange={(e) => handleInputChange({
                      name: "recurringDay",
                      value: e.value
                    })}
                    options={recurringDays}
                    optionLabel="name"
                    optionValue="value"
                    placeholder="Select Day"
                    className={`w-full p-0 bg-transparent border border-gray-300 text-white rounded-lg focus:outline-none focus:border-b-2 ${createTransactionData.recurringDay ? "asset_Dropdown" : ""}`}
                  />
                </div>
                {errors.recurringDay && <small className="text-red-500 text-center mt-[-10px]">{errors.recurringDay}</small>}
              </div>
            )}
          </div>

          <div className="mt-4 flex gap-2">
          <button
              disabled={loading}
              onClick={handleSave}
              className={`bg-secondary text-primary px-4 py-2 rounded ${loading ? 'bg-gray-600 opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Saving...' : actionTypes === "Create" ? 'Save' : 'Update'}
            </button>
            <button
              onClick={handleClose}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTransactionModal;
